import {
    useFloating,
} from '@pb/kit-component-floating';

import {getItemsHeight} from './utils/get-items-height';

import type {Placement} from '@floating-ui/react';
import type {UseFloatingProps, UseFloatingReturnType, getFloatingElPropsReturnType} from '@pb/kit-component-floating';
import type {ComponentProps} from 'react';


const placements = {
    left: ['bottom-end', 'top-end'] as Placement[],
    right: ['bottom-start', 'top-start'] as Placement[],
};
const defaultAllowedPlacements = placements.right;

interface getListBoxPropsReturnType extends getFloatingElPropsReturnType {
    activeIndex: number | null,
    getItemProps: UseFloatingReturnType['getItemProps'],
    floatingElRef: (node: HTMLElement | null) => void,
}
export interface UseListboxProps extends UseFloatingProps {
    /**
     * When set, the listbox width is no longer tied to the anchor width but
     * instead extends in the specified direction: `"left"` aligns to the
     * right edge of the anchor, `"right"` aligns to the left edge.
     */
    direction?: 'left' | 'right',
    /**
     * Number of items visible before the listbox starts scrolling. Controls
     * the `maxHeight` of the floating panel. Default `10`.
     */
    itemsForScroll?: number,
}

type _UseFloatingReturnType = Omit<UseFloatingReturnType, 'getFloatingElProps' | 'activeIndex' | 'getItemProps' | 'floatingElRef' | 'setActiveIndex'>;

interface UseListboxReturnType extends _UseFloatingReturnType {
    getListBoxProps: (nativeProps?: ComponentProps<'div'>) => getListBoxPropsReturnType,
    setActiveIndex: (index: (number | null)) => void,
}

/**
 * Sets up positioning, virtual keyboard navigation, and ARIA props for a
 * `<Listbox>` component.
 *
 * Returns `getListBoxProps()` — spread its result onto `<Listbox>` — and
 * `setActiveIndex` for programmatic focus control. All `UseFloatingProps` are
 * forwarded to the underlying `useFloating` hook.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-listbox--documentation}
 */
export function useListbox({
    direction,
    itemsForScroll = 10,
    allowedPlacements = direction ? placements[direction] : defaultAllowedPlacements,
    clickEnabled = true,
    listNavigationEnabled = true,
    equalWidth = !direction,
    typeaheadEnabled,
    maxHeight = getItemsHeight(itemsForScroll),
    role = 'combobox',
    listNavigationProps,
    ...restProps
}: UseListboxProps): UseListboxReturnType {

    const {
        getFloatingElProps,
        activeIndex,
        setActiveIndex,
        getItemProps,
        floatingElRef,
        ...rest
    } = useFloating({
        clickEnabled,
        listNavigationEnabled,
        typeaheadEnabled,
        allowedPlacements,
        equalWidth,
        maxHeight,
        role,
        listNavigationProps: {
            virtual: true,
            nested: false,
            ...listNavigationProps,
        },
        ...restProps,
    });

    return {
        getListBoxProps: nativeProps => ({
            activeIndex,
            getItemProps,
            floatingElRef,
            ...getFloatingElProps(nativeProps),
        }),
        setActiveIndex,
        ...rest,
    };
}
